import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, ReferenceArea } from 'recharts';

export const TrajectoryChart = React.memo(function TrajectoryChart({ shotGroups, selectedShot, distanceType = 'carry' }) {
  // Memoize expensive trajectory calculations
  const { groupTrajectories, mergedData, selectedShotTrajectory } = useMemo(() => {
    const generateTrajectoryPoints = (distance, peakHeight) => {
      const points = [];
      const numPoints = 30;

      for (let i = 0; i <= numPoints; i++) {
        const x = (distance * i) / numPoints;
        // Parabola formula: y = (4h/d²) * x * (d - x)
        const y = (4 * peakHeight) / (distance * distance) * x * (distance - x);
        points.push({ distance: x, height: y });
      }

      return points;
    };

    // Calculate average trajectory per group (performance optimization)
    const trajectories = shotGroups.map(group => {
      const includedShots = group.shots.filter(s => {
        const distance = distanceType === 'carry' ? s.metrics.carry : s.metrics.total;
        return s.included && distance && s.metrics.height;
      });

      if (includedShots.length === 0) return null;

      // Calculate average distance (carry or total) and height
      const distanceKey = distanceType === 'carry' ? 'carry' : 'total';
      const avgDistance = includedShots.reduce((sum, s) => sum + s.metrics[distanceKey], 0) / includedShots.length;
      const avgHeight = includedShots.reduce((sum, s) => sum + s.metrics.height, 0) / includedShots.length;

      // Also track carry and total for roll visualization
      const avgCarry = includedShots.reduce((sum, s) => sum + s.metrics.carry, 0) / includedShots.length;
      const avgTotal = includedShots.reduce((sum, s) => sum + s.metrics.total, 0) / includedShots.length;

      return {
        id: group.id,
        groupName: group.name,
        color: group.color,
        shotCount: includedShots.length,
        points: generateTrajectoryPoints(avgDistance, avgHeight),
        avgCarry,
        avgTotal,
      };
    }).filter(Boolean);

    if (trajectories.length === 0) {
      return { groupTrajectories: [], mergedData: [] };
    }

    // Optimized data merging - O(n) complexity
    const maxDistance = Math.max(...trajectories.flatMap(g => g.points.map(p => p.distance)));
    const merged = [];

    for (let i = 0; i <= 30; i++) {
      const distance = (maxDistance * i) / 30;
      const point = { distance };

      trajectories.forEach(traj => {
        // Find closest point in this trajectory
        const closest = traj.points.reduce((prev, curr) =>
          Math.abs(curr.distance - distance) < Math.abs(prev.distance - distance) ? curr : prev
        );
        point[traj.id] = closest.height;
      });

      merged.push(point);
    }

    // Generate trajectory for selected shot if any
    let selectedTraj = null;
    if (selectedShot) {
      const selectedGroup = shotGroups.find(g => g.id === selectedShot.groupId);
      const shot = selectedGroup?.shots.find(s => s.shotNumber === selectedShot.shotNumber);
      const shotDistance = distanceType === 'carry' ? shot?.metrics.carry : shot?.metrics.total;

      if (shotDistance && shot?.metrics.height) {
        const shotPoints = generateTrajectoryPoints(shotDistance, shot.metrics.height);

        // Merge selected shot trajectory into data
        shotPoints.forEach((point, i) => {
          if (merged[i]) {
            merged[i].selectedShot = point.height;
          }
        });

        selectedTraj = {
          groupId: selectedShot.groupId,
          shotNumber: selectedShot.shotNumber,
          color: selectedGroup.color,
        };
      }
    }

    return { groupTrajectories: trajectories, mergedData: merged, selectedShotTrajectory: selectedTraj };
  }, [shotGroups, selectedShot, distanceType]);

  // Empty state handling
  if (groupTrajectories.length === 0) {
    return (
      <div className="flex items-center justify-center h-[500px] text-zinc-600 bg-zinc-900/50 rounded-lg border border-zinc-800">
        <div className="text-center">
          <p className="text-lg font-semibold mb-2 text-zinc-400">No trajectory data available</p>
          <p className="text-sm text-zinc-500">Upload shots with carry and height data to see trajectories</p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={500}>
      <LineChart data={mergedData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
        <XAxis
          dataKey="distance"
          label={{ value: `Distance - ${distanceType === 'carry' ? 'Carry' : 'Total'} (yards)`, position: 'insideBottom', offset: -10, fill: '#a1a1aa' }}
          stroke="#71717a"
          tick={{ fill: '#a1a1aa' }}
        />
        <YAxis
          label={{ value: 'Height (feet)', angle: -90, position: 'insideLeft', fill: '#a1a1aa' }}
          stroke="#71717a"
          tick={{ fill: '#a1a1aa' }}
        />
        <Tooltip
          contentStyle={{ backgroundColor: 'rgba(24, 24, 27, 0.98)', border: '1px solid #3f3f46', color: '#fafafa' }}
          formatter={(value) => value ? `${value.toFixed(1)} ft` : null}
        />
        <Legend
          verticalAlign="top"
          height={36}
          wrapperStyle={{ color: '#fafafa' }}
          formatter={(value) => {
            const traj = groupTrajectories.find(t => t.id === value);
            return traj ? `${traj.groupName} (${traj.shotCount} shots avg)` : value;
          }}
        />

        {groupTrajectories.map(traj => (
          <Line
            key={traj.id}
            type="monotone"
            dataKey={traj.id}
            stroke={traj.color}
            strokeWidth={3}
            dot={false}
            name={traj.id}
            connectNulls
          />
        ))}

        {/* Selected shot trajectory - highlighted with dashed line */}
        {selectedShotTrajectory && (
          <Line
            key="selectedShot"
            type="monotone"
            dataKey="selectedShot"
            stroke={selectedShotTrajectory.color}
            strokeWidth={4}
            strokeDasharray="5 5"
            dot={false}
            name={`Shot #${selectedShotTrajectory.shotNumber} (Selected)`}
            connectNulls
          />
        )}

        {/* Carry landing + roll visualization for each group */}
        {groupTrajectories.map(traj => {
          const rollDistance = traj.avgTotal - traj.avgCarry;
          return (
            <React.Fragment key={`landing-${traj.id}`}>
              {/* Carry landing point - vertical dashed line */}
              <ReferenceLine
                x={traj.avgCarry}
                stroke={traj.color}
                strokeWidth={2}
                strokeDasharray="3 3"
                label={{
                  value: `${traj.groupName} Landing`,
                  position: 'top',
                  fill: traj.color,
                  fontSize: 10,
                }}
              />
              {/* Roll distance - shaded area */}
              {rollDistance > 0 && (
                <ReferenceArea
                  x1={traj.avgCarry}
                  x2={traj.avgTotal}
                  y1={0}
                  y2={5}
                  fill={traj.color}
                  fillOpacity={0.2}
                  strokeOpacity={0.3}
                  label={{
                    value: `+${rollDistance.toFixed(0)} roll`,
                    position: 'insideBottom',
                    fill: traj.color,
                    fontSize: 9,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </LineChart>
    </ResponsiveContainer>
  );
});

TrajectoryChart.propTypes = {
  shotGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      shots: PropTypes.arrayOf(
        PropTypes.shape({
          included: PropTypes.bool.isRequired,
          shotNumber: PropTypes.number.isRequired,
          metrics: PropTypes.shape({
            carry: PropTypes.number,
            total: PropTypes.number,
            height: PropTypes.number,
          }).isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  selectedShot: PropTypes.shape({
    groupId: PropTypes.string,
    shotNumber: PropTypes.number,
  }),
  distanceType: PropTypes.oneOf(['carry', 'total']),
};
